import { useEffect, useRef, useState } from 'react';

/**
 * Hook for scroll-triggered fade-in animations
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Visibility threshold (0-1)
 * @param {string} options.rootMargin - Margin around root
 * @returns {Object} - { ref, isVisible }
 */
export const useScrollAnimation = (options = {}) => {
  const { threshold = 0.1, rootMargin = '0px 0px -50px 0px' } = options;
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Once visible, stop observing
          if (ref.current) {
            observer.unobserve(ref.current);
          }
        }
      },
      { threshold, rootMargin }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, rootMargin]);

  return { ref, isVisible };
};

/**
 * Animated section wrapper component
 * Usage: <AnimatedSection className="...">content</AnimatedSection>
 */
export const AnimatedSection = ({ 
  children, 
  className = '', 
  delay = 0,
  direction = 'up', // 'up', 'down', 'left', 'right', 'scale'
  animation = 'fade-up', // kept for compatibility
  immediate = false // If true, animate immediately without waiting for scroll
}) => {
  const { ref, isVisible } = useScrollAnimation();
  const [mounted, setMounted] = useState(false);

  // For immediate animations (hero sections), trigger after mount
  useEffect(() => {
    if (immediate) {
      const timer = setTimeout(() => setMounted(true), 50);
      return () => clearTimeout(timer);
    }
  }, [immediate]);

  const shouldShow = immediate ? mounted : isVisible;

  const getInitialTransform = () => {
    switch (direction) {
      case 'up': return 'translateY(40px)';
      case 'down': return 'translateY(-40px)';
      case 'left': return 'translateX(40px)';
      case 'right': return 'translateX(-40px)';
      case 'scale': return 'scale(0.95)';
      default: return 'translateY(40px)';
    }
  };

  return (
    <div
      ref={immediate ? null : ref}
      className={className}
      style={{
        opacity: shouldShow ? 1 : 0,
        transform: shouldShow ? 'translateY(0) translateX(0) scale(1)' : getInitialTransform(),
        transition: `opacity 0.8s ease-out ${delay}ms, transform 0.8s ease-out ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

/**
 * Staggered children animation wrapper
 * Each child animates with increasing delay
 */
export const StaggeredContainer = ({ 
  children, 
  className = '',
  staggerDelay = 0.1,
  baseDelay = 0
}) => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <div ref={ref} className={className}>
      {React.Children.map(children, (child, index) => (
        <div
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: `opacity 0.6s ease-out ${baseDelay + index * staggerDelay}s, transform 0.6s ease-out ${baseDelay + index * staggerDelay}s`,
          }}
        >
          {child}
        </div>
      ))}
    </div>
  );
};

export default useScrollAnimation;
